import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface HealthStatus {
  name: string;
  status: 'healthy' | 'warning' | 'critical';
  message: string;
  value?: string | number;
  icon: string;
}

export const AdminHealthDashboard: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSystemHealth();
    const interval = setInterval(checkSystemHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const checkSystemHealth = async () => {
    try {
      const healthStatus: HealthStatus[] = [];

      // Check database connection
      try {
        const { error } = await supabase.from('profiles').select('id', { count: 'exact', head: true });
        healthStatus.push({
          name: 'Database',
          status: error ? 'critical' : 'healthy',
          message: error ? `Connection error: ${error.message}` : 'Connected',
          icon: 'database',
        });
      } catch (e) {
        healthStatus.push({
          name: 'Database',
          status: 'critical',
          message: 'Failed to connect',
          icon: 'database',
        });
      }

      // Check user count
      try {
        const { count } = await supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true });
        
        healthStatus.push({
          name: 'Total Users',
          status: 'healthy',
          message: 'System operational',
          value: count || 0,
          icon: 'people',
        });
      } catch (e) {
        healthStatus.push({
          name: 'Total Users',
          status: 'warning',
          message: 'Unable to fetch count',
          icon: 'people',
        });
      }

      // Check subscriptions
      try {
        const { count } = await supabase
          .from('subscriptions')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'active');
        
        healthStatus.push({
          name: 'Active Subscriptions',
          status: count! > 0 ? 'healthy' : 'warning',
          message: count! > 0 ? 'Revenue flowing' : 'No active subscriptions',
          value: count || 0,
          icon: 'subscriptions',
        });
      } catch (e) {
        healthStatus.push({
          name: 'Active Subscriptions',
          status: 'warning',
          message: 'Unable to fetch',
          icon: 'subscriptions',
        });
      }

      // Check email delivery rate
      try {
        const { data: emailStats } = await supabase
          .from('notifications')
          .select('id, status', { count: 'exact' })
          .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

        if (emailStats && emailStats.length > 0) {
          const successRate = (emailStats.filter(e => e.status === 'sent').length / emailStats.length) * 100;
          healthStatus.push({
            name: 'Email Delivery',
            status: successRate > 95 ? 'healthy' : successRate > 80 ? 'warning' : 'critical',
            message: `${successRate.toFixed(1)}% delivered in 24h`,
            value: `${successRate.toFixed(0)}%`,
            icon: 'mail',
          });
        } else {
          healthStatus.push({
            name: 'Email Delivery',
            status: 'healthy',
            message: 'No recent emails',
            icon: 'mail',
          });
        }
      } catch (e) {
        healthStatus.push({
          name: 'Email Delivery',
          status: 'warning',
          message: 'Unable to calculate',
          icon: 'mail',
        });
      }

      // Check storage usage (estimated)
      try {
        const { data: docs } = await supabase
          .from('documents')
          .select('file_size', { count: 'exact' });

        const totalSize = (docs?.reduce((sum, d) => sum + (d.file_size || 0), 0) || 0) / (1024 * 1024 * 1024); // GB
        const limit = 100; // Assuming 100GB limit
        const usagePercent = (totalSize / limit) * 100;

        healthStatus.push({
          name: 'Storage Usage',
          status: usagePercent > 90 ? 'critical' : usagePercent > 70 ? 'warning' : 'healthy',
          message: `${totalSize.toFixed(1)}GB of ${limit}GB used`,
          value: `${usagePercent.toFixed(0)}%`,
          icon: 'storage',
        });
      } catch (e) {
        healthStatus.push({
          name: 'Storage Usage',
          status: 'warning',
          message: 'Unable to calculate',
          icon: 'storage',
        });
      }

      // Check API latency (simulated)
      const startTime = performance.now();
      try {
        await supabase.from('profiles').select('id', { count: 'exact', head: true });
        const latency = Math.round(performance.now() - startTime);
        
        healthStatus.push({
          name: 'API Latency',
          status: latency > 500 ? 'critical' : latency > 200 ? 'warning' : 'healthy',
          message: `${latency}ms average`,
          value: `${latency}ms`,
          icon: 'speed',
        });
      } catch (e) {
        healthStatus.push({
          name: 'API Latency',
          status: 'critical',
          message: 'Unable to measure',
          icon: 'speed',
        });
      }

      setHealth(healthStatus);
    } catch (error) {
      console.error('Error checking system health:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: HealthStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'critical':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    }
  };

  const getStatusTextColor = (status: HealthStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'text-green-800 dark:text-green-200';
      case 'warning':
        return 'text-yellow-800 dark:text-yellow-200';
      case 'critical':
        return 'text-red-800 dark:text-red-200';
    }
  };

  const getStatusIcon = (status: HealthStatus['status']) => {
    switch (status) {
      case 'healthy':
        return '✓';
      case 'warning':
        return '⚠️';
      case 'critical':
        return '❌';
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-secondary/30 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark">System Health Status</h3>
        <button
          onClick={checkSystemHealth}
          className="text-xs px-2 py-1 rounded border border-luna-secondary/30 text-luna-secondary hover:bg-luna-secondary/10"
        >
          Check Now
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {health.map((item) => (
          <div
            key={item.name}
            className={`rounded-xl border-2 p-4 ${getStatusColor(item.status)}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-xl">{item.icon}</span>
                <h4 className={`font-semibold ${getStatusTextColor(item.status)}`}>{item.name}</h4>
              </div>
              <span className="text-lg">{getStatusIcon(item.status)}</span>
            </div>
            <p className={`text-sm ${getStatusTextColor(item.status)}`}>{item.message}</p>
            {item.value && (
              <p className={`text-xs font-bold mt-2 ${getStatusTextColor(item.status)}`}>{item.value}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminHealthDashboard;
